import { HttpClient, HttpHeaders, HttpUrlEncodingCodec } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OccEndpointsService } from "@spartacus/core";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";


export interface PunchOutSession {
  customerId: string;
  cartId: string;
  punchOutLevel: string;
  punchOutOperation: string;
  selectedItem: string;
  token: PunchOutToken;
}

export interface PunchOutToken {
  accessToken: String;
  tokenType: String;
}

/**
 * Name of the lightweight flag cookie that signals "this is a punchout
 * session" to the Next.js storefront, mirroring `vsca_auth_hint`
 * (my-auth-state-persistence-service.service.ts). Holds no session data,
 * only a boolean presence flag, so Next.js can filter CMS menu links for
 * punchout users without needing the punchout session itself.
 */
const PUNCHOUT_HINT_COOKIE_NAME = 'vsca_punchout_hint';

/**
 * Matches the backend's `b2bpunchout.timeout` (1 hour), giving the cookie a
 * hard ceiling even if it's never cleared. It is also actively cleared on
 * logout by `MyAuthStatePersistenceService`, which deletes it in lockstep
 * with `vsca_auth_hint` whenever the Spartacus access token goes away.
 */
const PUNCHOUT_HINT_MAX_AGE_MS = 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class PunchOutService {
  constructor(
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService
  ) { }

  getPunchOutSessionData(
    sid: string,
    key: string
  ): Observable<PunchOutSession> {
    ['isLoggedInPayer','defaultLocInList','defaultLocation','selectedLocations','productAllocations'].forEach(token => sessionStorage.removeItem(token));
    ['formularyCustomer', 'YFAuthorized', 'blockOrder','punchout-sid-key','spartacus⚿vsh⚿cart','spartacus⚿vax⚿cart'].forEach(token => localStorage.removeItem(token));


    localStorage.setItem('punchout-sid-key', sid);
    const codec = new HttpUrlEncodingCodec();
    const keyValue = encodeURIComponent(key);
    const sidValue = encodeURIComponent(sid);
    const url = this.occEndpoints.buildUrl('punchout/session/punchoutlogin');
    return this.http.post<any>(
      url,
      {
        key: keyValue,
        sid: sidValue,
      },
      httpOptions
    ).pipe(
      tap(() => this.setPunchoutHintCookie()),
      catchError((error) => {
        this.clearPunchoutHintCookie();
        return throwError(() => error);
      })
    );
  }

  /**
   * Sets `vsca_punchout_hint=1`, non-`HttpOnly` and host-only (no `Domain=`)
   * since Spartacus and the Next.js storefront share the same host per
   * environment — same scoping as `vsca_auth_hint`. Only called once the
   * punchout login request has actually succeeded.
   */
  private setPunchoutHintCookie(): void {
    if (typeof document === 'undefined') return;

    const expires = new Date(Date.now() + PUNCHOUT_HINT_MAX_AGE_MS).toUTCString();
    document.cookie = `${PUNCHOUT_HINT_COOKIE_NAME}=1; ${this.cookieAttrs()}; Expires=${expires}`;
  }

  /**
   * Clears `vsca_punchout_hint` if a punchout login attempt fails after the
   * request was already in flight, so a failed login never leaves the hint
   * pointing at a session that doesn't exist.
   */
  private clearPunchoutHintCookie(): void {
    if (typeof document === 'undefined') return;

    document.cookie = `${PUNCHOUT_HINT_COOKIE_NAME}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; ${this.cookieAttrs()}`;
  }

  private cookieAttrs(): string {
    return `Path=/; SameSite=Lax${
      typeof location !== 'undefined' && location.protocol === 'https:'
        ? '; Secure'
        : ''
    }`;
  }
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

